import { useState } from 'react';
import './App.css';

// Import components
import PropertyForm from './components/PropertyForm';
import MarketComps from './components/MarketComps';
import Justification from './components/Justification';
import FeedbackWidget from './components/FeedbackWidget';

// Import utilities
import { calculateSweetSpot } from './utils/pricing-algorithm';
import { estimateSqft } from './utils/sqft-utils';
import { getMarketData } from './data/market-data';
import { generateRepresentativeComps, getCompsSummary } from './utils/comps-generator';

// Import API service
import { useStaticData, fetchMarketData, clearCache, formatCacheTimestamp } from './services/rentcast';

function App() {
  const [result, setResult] = useState(null);
  const [comps, setComps] = useState([]);
  const [compsSummary, setCompsSummary] = useState(null);
  const [marketDataInfo, setMarketDataInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isStale, setIsStale] = useState(false);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState(null);
  const [cacheTimestamp, setCacheTimestamp] = useState(null);
  const [lastQuery, setLastQuery] = useState(null); // Track last query for refresh

  // Check if using static data
  const isStaticMode = useStaticData();

  // Called when form fields change
  const handleFormChange = () => {
    if (result) {
      setIsStale(true);
    }
    // Clear any previous errors
    if (error) {
      setError(null);
    }
  };

  // Clear all results
  const handleReset = () => {
    setResult(null);
    setComps([]);
    setCompsSummary(null);
    setMarketDataInfo(null);
    setIsStale(false);
    setError(null);
    setDataSource(null);
    setCacheTimestamp(null);
    setLastQuery(null);
  };

  // Refresh data (clear cache and re-fetch)
  const handleRefresh = async () => {
    if (!lastQuery) return;

    const { zipCode, bedrooms } = lastQuery;
    clearCache(zipCode, bedrooms);

    // Re-run the calculation with force refresh
    setIsLoading(true);
    setError(null);

    try {
      const apiResult = await fetchMarketData(zipCode, bedrooms, true);
      const marketData = apiResult.data;

      if (!marketData.medianRent) {
        throw new Error('No rental data returned from API');
      }

      // Rebuild property and recalculate
      const property = { ...lastQuery.property };
      const pricing = calculateSweetSpot(property, marketData);
      const generatedComps = generateRepresentativeComps(marketData, bedrooms, property.sqft);
      const summary = getCompsSummary(generatedComps);

      setResult({ ...pricing, neighborhood: marketData.neighborhood });
      setComps(generatedComps);
      setCompsSummary(summary);
      setMarketDataInfo(marketData);
      setDataSource('live');
      setCacheTimestamp(apiResult.timestamp);
      setIsStale(false);
    } catch (err) {
      setError(`Refresh failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const gtag = (...args) => window.gtag?.(...args);

  // Handle form submission
  const handleCalculate = async (formData) => {
    console.log('Form submitted:', formData);
    setIsLoading(true);
    setError(null);

    const bedrooms = parseInt(formData.bedrooms);
    const bathrooms = parseFloat(formData.bathrooms);
    const sqft = estimateSqft(bedrooms, formData.sizeCategory);
    const zipCode = formData.zipCode;

    gtag('event', 'calculate_rent', {
      event_category: 'engagement',
      zip_code: zipCode,
      bedrooms,
      bathrooms,
      condition: formData.condition,
      utilities: formData.utilities,
    });

    // Build property object for pricing calculation
    const property = {
      zipCode: zipCode,
      bedrooms: bedrooms,
      bathrooms: bathrooms,
      sqft: sqft,
      condition: formData.condition,
      utilities: formData.utilities,
      amenities: {
        parking: formData.parking,
        laundry: formData.laundry,
        outdoor: formData.outdoor,
        updated: formData.updated,
        petFriendly: formData.petFriendly,
        dishwasher: formData.dishwasher,
        hvac: formData.hvac
      }
    };

    // Store query for refresh functionality
    setLastQuery({ zipCode, bedrooms, property });

    // Try API first if configured
    if (!isStaticMode) {
      try {
        console.log('Fetching market data from Rentcast API...');
        const apiResult = await fetchMarketData(zipCode, bedrooms);
        const marketData = apiResult.data;

        // Check if we got valid rent data
        if (marketData.medianRent) {
          // Calculate pricing with API data
          const pricing = calculateSweetSpot(property, marketData);
          const generatedComps = generateRepresentativeComps(marketData, bedrooms, sqft);
          const summary = getCompsSummary(generatedComps);

          setResult({
            ...pricing,
            neighborhood: marketData.neighborhood
          });
          setComps(generatedComps);
          setCompsSummary(summary);
          setMarketDataInfo(marketData);
          setDataSource(apiResult.isFromCache ? 'cached' : 'live');
          setCacheTimestamp(apiResult.timestamp);
          setIsLoading(false);
          setIsStale(false);
          gtag('event', 'calculate_result', {
            event_category: 'engagement',
            data_source: apiResult.isFromCache ? 'cached' : 'live',
            zip_code: zipCode,
            bedrooms,
            suggested_price: pricing.sweetSpot,
          });
          return;
        } else {
          console.warn('API returned no rent data, falling back to static');
          setError('API returned incomplete data. Using local market data.');
        }
      } catch (apiError) {
        console.warn('API error, falling back to static data:', apiError.message);
        setError(`API unavailable: ${apiError.message}. Using local market data.`);
        // Fall through to static data
      }
    }

    // Use static data (default or fallback)
    setTimeout(() => {
      const marketData = getMarketData(property.zipCode, property.bedrooms);

      if (!marketData) {
        setError('No market data available for this zip code and bedroom count. Try a different neighborhood.');
        setIsLoading(false);
        return;
      }

      // Calculate pricing
      const pricing = calculateSweetSpot(property, marketData);
      console.log('Pricing result:', pricing);

      // Generate market comps
      const generatedComps = generateRepresentativeComps(marketData, bedrooms, sqft);
      const summary = getCompsSummary(generatedComps);

      setResult({
        ...pricing,
        neighborhood: marketData.neighborhood
      });
      setComps(generatedComps);
      setCompsSummary(summary);
      setMarketDataInfo(marketData);
      setDataSource('static');
      setCacheTimestamp(null);
      setIsLoading(false);
      setIsStale(false);
      gtag('event', 'calculate_result', {
        event_category: 'engagement',
        data_source: 'static',
        zip_code: zipCode,
        bedrooms,
        suggested_price: pricing.sweetSpot,
      });
    }, 300);
  };

  return (
    <div className="app">
      {/* Header */}
      <header className="app-header">
        <h1><span className="title-accent">PHL</span>Renting</h1>
        <p className="tagline">Know what your property is worth.</p>
        <p className="subtitle">Market-backed pricing for Philadelphia landlords</p>
      </header>

      {/* Main content - two column layout */}
      <main className="app-main">
        {/* Left column - Property Form */}
        <section className="column column-form">
          <div className="card">
            <h2>Property Details</h2>
            <PropertyForm onCalculate={handleCalculate} onChange={handleFormChange} isLoading={isLoading} />
          </div>
        </section>

        {/* Right column - Results */}
        <section className="column column-results">
          <div className="card">
            <h2>Pricing Results</h2>
            {/* Error message */}
            {error && !result && (
              <div className="error-banner">
                <p>{error}</p>
              </div>
            )}

            {result ? (
              <div className={`results-display ${isStale ? 'stale' : ''}`}>
                {isStale && (
                  <div className="stale-overlay">
                    <p>Form changed</p>
                    <span>Click "Calculate Rent" to update results</span>
                    <button className="reset-btn" onClick={handleReset}>
                      Clear Results
                    </button>
                  </div>
                )}

                {/* Data source indicator with timestamp */}
                {dataSource && (
                  <div className="data-source-row">
                    <div className={`data-source-badge ${dataSource}`}>
                      {dataSource === 'live' && 'Live API Data'}
                      {dataSource === 'cached' && 'Cached API Data'}
                      {dataSource === 'static' && 'Local Market Data'}
                    </div>
                    {cacheTimestamp && (
                      <span className="cache-timestamp">
                        Updated: {formatCacheTimestamp(cacheTimestamp)}
                      </span>
                    )}
                    {(dataSource === 'cached' || dataSource === 'live') && (
                      <button
                        type="button"
                        className="refresh-btn"
                        onClick={handleRefresh}
                        disabled={isLoading}
                        title="Fetch fresh data from API"
                      >
                        Refresh
                      </button>
                    )}
                  </div>
                )}

                {/* Warning if API failed but got static fallback */}
                {error && (
                  <div className="warning-banner">
                    <p>{error}</p>
                  </div>
                )}

                <div className="price-display">
                  <span className="label">Recommended Rent</span>
                  <span className="price">${result.sweetSpot.toLocaleString()}/mo</span>
                </div>

                <div className="result-row">
                  <span className="label">Neighborhood:</span>
                  <span>{result.neighborhood}</span>
                </div>

                <div className="result-row">
                  <span className="label">Market Position:</span>
                  <span className={`position ${result.percentDiff > 0 ? 'above' : 'below'}`}>
                    {result.marketPosition} ({result.percentDiff > 0 ? '+' : ''}{result.percentDiff}%)
                  </span>
                </div>

                <div className="result-row">
                  <span className="label">Base Market Rent:</span>
                  <span>${result.baseRent.toLocaleString()}</span>
                </div>

                <div className="result-row">
                  <span className="label">Total Adjustments:</span>
                  <span className={result.totalAdjustment >= 0 ? 'positive' : 'negative'}>
                    {result.totalAdjustment >= 0 ? '+' : ''}${Math.round(result.totalAdjustment)}
                  </span>
                </div>

                {/* HUD FMR Base - this is now the pricing foundation */}
                {result.hudFmr && (
                  <div className="hud-fmr-display">
                    <div className="result-row">
                      <span className="label">HUD FMR Base ({result.metadata.bedrooms}BR):</span>
                      <span>${result.hudFmr.amount.toLocaleString()}</span>
                    </div>
                    {result.marketReference && (
                      <div className="result-row market-ref">
                        <span className="label">Market Reference:</span>
                        <span>${result.marketReference.median.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="range-display">
                  <h3>Pricing Range</h3>
                  <div className="range-row">
                    <span className="range-label">Conservative:</span>
                    <span className="range-value">${result.range.conservative.toLocaleString()}</span>
                  </div>
                  <div className="range-row optimal">
                    <span className="range-label">Optimal:</span>
                    <span className="range-value">${result.range.optimal.toLocaleString()}</span>
                  </div>
                  <div className="range-row">
                    <span className="range-label">Aggressive:</span>
                    <span className="range-value">${result.range.aggressive.toLocaleString()}</span>
                  </div>
                </div>

                {/* Adjustments Breakdown */}
                {result.adjustments && result.adjustments.length > 0 && (
                  <div className="adjustments-display">
                    <h3>Price Adjustments</h3>
                    {result.adjustments.map((adj, index) => (
                      <div key={index} className={`adjustment-row ${adj.amount >= 0 ? 'positive' : 'negative'}`}>
                        <span className="adjustment-factor">{adj.factor}</span>
                        <span className="adjustment-amount">
                          {adj.amount >= 0 ? '+' : ''}${Math.round(adj.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Market Comps */}
                <MarketComps comps={comps} summary={compsSummary} />

                {/* Justification */}
                <Justification
                  justification={result.justification}
                  marketData={marketDataInfo}
                />

                {/* Feedback Widget */}
                <FeedbackWidget />
              </div>
            ) : (
              <div className="empty-state">
                <p>Enter property details and click "Calculate Rent" to see pricing recommendations.</p>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <p>Data based on Philadelphia rental market analysis</p>
        <div className="branding">
          <span>Developed by</span>
          <img src="/autorithm-logo.jpg" alt="Autorithm" className="branding-logo" />
        </div>
        <p className="footer-confidential">Confidential &mdash; Feedback Preview Only &mdash; &copy; 2026 Autorithm / phlrenting.com</p>
      </footer>
    </div>
  );
}

export default App;
